/**
 * CompleteProfileModal — non-dismissable modal shown after the first
 *  Google sign-in to collect the fields Google didn't share.
 *
 *  Rendering policy (see AdminLayout.jsx `needsCompleteProfile`):
 *    Mount when the user is a company_admin AND `user.googleLinked`
 *    AND `!user.profileComplete`.
 *    The modal renders OVER the dashboard (already loaded behind it)
 *    and blocks every interaction with the dashboard until the form
 *    submits successfully. After submit, `refreshUser()` re-fetches
 *    /auth/me which flips `profileComplete` to true, the AdminLayout
 *    gate sees the new value, and the modal unmounts.
 *
 *  No `onClose` prop on purpose — the modal must never be dismissed
 *  INTO the app, or the user lands on a dashboard with no organisation
 *  (and the gate would re-mount it immediately anyway).
 *  So the only way FORWARD is success, and the only way OUT is Cancel,
 *  which signs the user out and returns them to Sign In — the escape
 *  hatch someone who picked the wrong Google account actually needs.
 *  The account itself is left intact, so signing in with Google again
 *  simply resumes this form.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Building2, Mail, User as UserIcon } from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { PhoneInputField, validatePhone } from './ui/PhoneInputField'
import { useDetectedCountry } from '../lib/use-detected-country'

// Mirrors the server-side DTO (`completeProfileDto`).
//
// There is deliberately NO password field. This modal is only ever shown to an
// account the Google bootstrap created, and a successful Google authentication
// is already proof of identity — `googleSub` IS the credential. Making people
// invent a second TaskMagix password to finish signing up added friction to
// every Google sign-up, and the lockout risk it was hedging is already covered
// by self-service: Forgot Password works for a credential-less Identity, so a
// Google-only user can add a password whenever they actually want one.
//
// `passwordHash: null` next to a `googleSub` is a valid, fully supported
// account state — do not reintroduce a required password here without
// revisiting `changeOwnEmail`, which exempts those identities from the
// current-password check on the same reasoning.
const schema = z.object({
  organisationName: z
    .string()
    .trim()
    .min(1, 'Company Name is required.')
    .min(2, 'Company Name must be at least 2 characters.')
    .max(200, 'Company Name is too long.'),
  // PhoneInputField emits an E.164 string (e.g. `+919876543210`) or
  // an empty string while the user is typing. Run the same
  // `validatePhone` helper the Sign Up page uses so the rules stay
  // identical across both surfaces.
  contactNumber: z
    .string()
    .superRefine((v, ctx) => {
      const r = validatePhone(v)
      if (!r.valid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: r.message ?? 'Please enter a valid Contact Number.',
        })
      }
    }),
})

export function CompleteProfileModal() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const refreshUser = useAuthStore((s) => s.refreshUser)
  const signOut = useAuthStore((s) => s.signOut)
  const [submitting, setSubmitting] = useState(false)
  // Server IP-detected country → phone dial code (follows the VPN). Null while
  // loading / unresolvable, in which case the field keeps its own default.
  const detectedCountry = useDetectedCountry()
  const [cancelling, setCancelling] = useState(false)

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { organisationName: '', contactNumber: '' },
  })

  async function onSubmit(values) {
    setSubmitting(true)
    try {
      const trimmedPhone = (values.contactNumber ?? '').trim()
      await api.post('/auth/me/complete-profile', {
        organisationName: values.organisationName.trim(),
        // Optional — send `undefined` for empty so the backend's
        // optional DTO branch fires instead of trying to validate a
        // zero-length string as a phone number.
        contactNumber: trimmedPhone.length > 0 ? trimmedPhone : undefined,
        // No `password` key — Google authentication is the credential. The
        // backend DTO still accepts one if some other client sends it, but
        // this form never does, so the Identity keeps `passwordHash: null`.
      })
      toast.success('Profile completed. Welcome to TaskMagix!')
      // Re-fetch the user so `profileComplete: true` lands in the
      // store and the App.jsx gate unmounts this modal automatically.
      await refreshUser()
      // First-time Google SSO users have a tenant with
      // `planSelectedAt: null` — route them straight to /billing so
      // they can pick a plan. The PlanGate in App.jsx would do this
      // for them on the next navigation anyway, but the explicit
      // navigate keeps the post-Save flow snappy (no flash of the
      // dashboard before the redirect).
      navigate('/billing', { replace: true })
    } catch (err) {
      const { message, fields } = extractApiError(err)
      if (fields) {
        for (const [name, msg] of Object.entries(fields)) {
          form.setError(name, { message: msg })
        }
      } else {
        toast.error(message || 'Could not save your profile. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Escape hatch. Completing the profile is mandatory, so Cancel can't
   * just close the modal — the App.jsx gate would re-mount it on the
   * next render. Instead it signs the user out and returns them to Sign
   * In, which is what a user who signed in with the wrong Google account
   * needs (they can then pick a different one). Non-destructive: the
   * account is kept, so signing in again resumes this form.
   *
   * Mirrors the layouts' `handleSignOut` (signOut → toast → /sign-in).
   */
  async function handleCancel() {
    setCancelling(true)
    try {
      await signOut()
      toast.success('Signed out. You can sign in with a different account.')
      navigate('/sign-in', { replace: true })
    } catch {
      // `signOut` clears local state even when the API call fails, so
      // this is belt-and-braces — re-enable the buttons if it ever throws.
      setCancelling(false)
      toast.error('Could not sign you out. Please try again.')
    }
  }

  return (
    // z-[200] sits above the AdminLayout sidebar (no explicit z-index,
    // so `auto` from the stacking context) AND above the existing
    // billing-lock overlay (which uses ~z-50). Without this, sidebar
    // clicks would slip through and let the user navigate routes
    // while their profile is half-completed.
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={(e) => {
        // Defence-in-depth: if a stray click somehow lands on the
        // backdrop instead of the modal card, swallow it explicitly
        // so the sidebar underneath stays inert. The card stops
        // propagation on its own clicks below.
        if (e.target === e.currentTarget) e.stopPropagation()
      }}
    >
      {/* Backdrop — clicking it does NOT close the modal. Pointer
       *  events stay enabled so the dashboard behind can't be
       *  clicked through, but no close handler is attached. */}
      <div
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm"
        aria-hidden="true"
      />

      {/* Modal card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="complete-profile-title"
        className="relative bg-[color:var(--color-card)] rounded-2xl shadow-2xl w-full max-w-[520px] overflow-hidden"
      >
        {/* Header — TaskMagix-branded navy gradient mark matching the
         *  floating logo on the Sign In / Sign Up screens. Was
         *  previously a Google-blue "G" placeholder, which read as
         *  "this is Google's screen" instead of "this is TaskMagix
         *  finishing your signup". The `/login.png` asset is the same
         *  white swirl used on the auth split-screen, so first-time
         *  Google users see a consistent brand identity from sign-in
         *  → profile completion → dashboard. */}
        <div className="p-6 border-b border-[color:var(--color-rule)] bg-gradient-to-br from-[color:var(--color-accent-bg)] to-[color:var(--color-card)]">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 overflow-hidden border border-white/20 shadow-[0_4px_12px_-4px_rgba(2,54,123,0.35)]"
              style={{
                background:
                  'linear-gradient(135deg, #02367B 0%, #006CA5 100%)',
              }}
            >
              <img
                src="/login.png"
                alt="TaskMagix"
                className="h-8 w-8 object-contain"
              />
            </div>
            <div className="min-w-0">
              {/* "Signed in as <email>" microcopy was removed by
               *  request — the user's email is already shown in the
               *  read-only "From Google (verified)" block below, so
               *  repeating it in the header was duplicate context.
               *  The title now sits flush with the brand mark. */}
              <h2
                id="complete-profile-title"
                className="text-[20px] font-semibold text-[color:var(--color-ink-strong)]"
              >
                Complete your profile
              </h2>
            </div>
          </div>
          <p className="text-sm text-[color:var(--color-ink-muted)]">
            A few details Google didn&apos;t share — these are required before you can use TaskMagix.
          </p>
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Read-only block — communicates what we already know.
             *  Emphasis flipped vs the previous build: LABELS get
             *  `font-semibold` (the "Full Name" / "Email ID" callouts
             *  are now what the eye lands on), VALUES drop to the
             *  default body weight so the Google-supplied data reads
             *  as quiet reference. Label phrasing matches the
             *  canonical "Email ID" used everywhere else in the app
             *  (All Users, User Details modal, Sign Up). */}
            <div className="rounded-lg border border-[color:var(--color-rule)] p-4 bg-[color:var(--color-canvas)] space-y-2">
              <p className="text-[11px] uppercase tracking-wider font-bold text-[color:var(--color-ink-muted)] mb-2">
                From Google (verified)
              </p>
              <div className="flex items-center justify-between text-sm gap-3">
                <span className="font-semibold text-[color:var(--color-ink-strong)] flex items-center gap-1.5">
                  <UserIcon size={13} /> Full Name
                </span>
                <span className="text-[color:var(--color-ink)] truncate">
                  {user?.fullName || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm gap-3">
                <span className="font-semibold text-[color:var(--color-ink-strong)] flex items-center gap-1.5">
                  <Mail size={13} /> Email ID
                </span>
                <span className="text-[color:var(--color-ink)] truncate">
                  {user?.emailId || '—'}
                </span>
              </div>
            </div>

            {/* Company Name (required) */}
            <Field
              label="Company Name"
              required
              icon={<Building2 size={16} />}
              error={form.formState.errors.organisationName?.message}
            >
              <input
                type="text"
                // Matches the schema's own `.max(200)`. Without it a paste is
                // accepted silently and only rejected at submit, which is a
                // worse place to find out.
                maxLength={200}
                placeholder="Enter a Company Name"
                {...form.register('organisationName')}
                className="w-full h-11 pl-10 pr-3 border border-[color:var(--color-rule)] rounded-lg bg-[color:var(--color-card)] text-sm focus:outline-none focus:border-[color:var(--color-accent-strong)] focus:ring-2 focus:ring-[color:var(--color-accent-bg)]"
              />
            </Field>

            {/* Contact Number — OPTIONAL. Google doesn't share it,
             *  but we don't force the user to invent one before they
             *  can use TaskMagix (they can fill it in from the
             *  Profile page later). When the user does enter
             *  something, the same `validatePhone` rules the Sign Up
             *  page uses kick in for proper country-aware messages
             *  ("Indian numbers must start with 6-9.", etc.). Empty
             *  is accepted end-to-end — the schema's `validatePhone`
             *  helper short-circuits on empty values, the
             *  `onSubmit` strips the empty string to `undefined`,
             *  and the backend DTO accepts the omission too. */}
            <PhoneInputField
              // Remount once the IP country arrives so the new default dial
              // code applies; falls back to the field's own guess when null.
              key={detectedCountry ?? 'auto'}
              defaultCountry={detectedCountry ?? undefined}
              id="contact-number"
              label="Contact Number"
              helperText="Optional — you can add this later from your Profile."
              value={form.watch('contactNumber') ?? ''}
              onChange={(v) =>
                form.setValue('contactNumber', v ?? '', { shouldValidate: true })
              }
              error={form.formState.errors.contactNumber?.message}
            />
          </div>

          <div className="p-5 border-t border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] flex items-center justify-between gap-3">
            <p className="text-[11px] text-[color:var(--color-ink-subtle)] max-w-[220px]">
              TaskMagix needs these details to set up your workspace. Cancel
              signs you out so you can use a different account.
            </p>
            {/* `type="button"` is essential — a bare <button> inside this
             *  <form> defaults to type="submit" and would fire the form
             *  instead of cancelling. */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleCancel}
                disabled={submitting || cancelling}
                className="px-4 h-10 rounded-lg text-sm font-semibold text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink)] cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {cancelling ? 'Signing Out…' : 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={submitting || cancelling}
                className="px-5 h-10 rounded-lg text-sm font-semibold text-white shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background:
                    'linear-gradient(135deg,#0B2E5C 0%,#1E5BA8 50%,#0E9EC9 100%)',
                }}
              >
                {submitting ? 'Saving…' : 'Save & Continue →'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label, required, hint, icon, error, children, helper }) {
  return (
    <div>
      <label className="block text-[13px] font-semibold text-[color:var(--color-ink-strong)] mb-1.5">
        {label}
        {required ? (
          <span className="ml-0.5 text-[color:var(--color-priority-urgent)]">*</span>
        ) : null}
        {hint ? (
          <span className="ml-2 text-[11px] font-normal text-[color:var(--color-ink-subtle)]">
            ({hint})
          </span>
        ) : null}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)] pointer-events-none">
          {icon}
        </span>
        {children}
      </div>
      {error ? (
        <p className="mt-1.5 text-xs text-[color:var(--color-priority-urgent)]">
          {error}
        </p>
      ) : helper ? (
        // Helper line — quiet muted copy under the input that spells
        // out a policy / rule the user needs to follow. Suppressed
        // when an error is showing so the user sees the actionable
        // message first instead of two competing lines.
        <p className="mt-1.5 text-[11px] text-[color:var(--color-ink-muted)]">
          {helper}
        </p>
      ) : null}
    </div>
  )
}
